import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { CircleQuestionOptions, CircleSpec, ProblemData, SUPERSCRIPT } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { circlePrompts as en } from "../Files/Prompts/en/circles.ts";
import { circlePrompts as es } from "../Files/Prompts/es/circles.ts";
import { circlePrompts as fr } from "../Files/Prompts/fr/circles.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Circles with pi as 3.14. See ADR 0106.
 *
 * **The arithmetic is integer hundredths.** Pi is 314 hundredths, so
 * C = 3.14 x d is `314 * d` hundredths and A = 3.14 x r x r is `314 * r * r`,
 * and the answer is printed from that integer. Multiplying floats gives
 * 3.14 x 7 = 21.980000000000004; this cannot. Radii are whole numbers, so
 * every answer has at most two decimal places and is exact for the question as
 * set (ADR 0106 §Context).
 *
 * **The radius is drawn first** (1 up to the dial) and everything else is
 * derived from it: a diameter given is `2 * r`, so it is always even and a
 * student who halves it gets a whole radius, and the Diameter from
 * Circumference question builds its circumference as 3.14 x d, so the answer is
 * the whole number d. Neighbouring problems never share a radius: `build`
 * redraws against the radius of the problem before it.
 *
 * None of this is visible in the output. `CircleGenerator.test.ts` says so.
 */

/** Pi, in hundredths (ADR 0106 §3). */
export const PI_HUNDREDTHS = 314;

export const LENGTH_UNIT = "cm";
/**
 * A no-break space (in WinAnsi), so a prompt never leaves "cm." alone at the
 * start of a line after the circumference it belongs to.
 */
const NO_BREAK_SPACE = "\u00a0";
const AREA_UNIT = `cm${SUPERSCRIPT[2]}`;

/** The smallest radius a circle may have. */
export const SMALLEST_RADIUS = 1;

function questionTypes(question: CircleQuestionOptions): CircleQuestionOptions[] {
    if (question === CircleQuestionOptions.Mixed) {
        return [
            CircleQuestionOptions.Circumference,
            CircleQuestionOptions.Area,
            CircleQuestionOptions.DiameterFromCircumference,
        ];
    }
    return [question];
}

function usableMax(maxRadius: number): number {
    return Math.max(SMALLEST_RADIUS, Math.floor(maxRadius) || 10);
}

/**
 * A radius from 1 to `max`, not `avoid`. Drawn from one fewer value and
 * stepped over `avoid`, so the draw stays uniform and takes one call. With a
 * single radius available there is nothing to step to, so it repeats.
 */
function radiusFor(max: number, avoid?: number): number {
    if (avoid === undefined || avoid < SMALLEST_RADIUS || avoid > max || max === SMALLEST_RADIUS) {
        return getRandomInt(SMALLEST_RADIUS, max);
    }
    const draw = getRandomInt(SMALLEST_RADIUS, max - 1);
    return draw >= avoid ? draw + 1 : draw;
}

/** Circumference in hundredths of a cm: 3.14 x diameter. */
export function circumferenceHundredths(radius: number): number {
    return PI_HUNDREDTHS * 2 * radius;
}

/** Area in hundredths of a cm squared: 3.14 x radius x radius. */
export function areaHundredths(radius: number): number {
    return PI_HUNDREDTHS * radius * radius;
}

/**
 * One circle problem. Always succeeds.
 *
 * The drawing rides on `circle`; `display` carries the prompt and the answer,
 * and the answer includes its unit because the unit is the part this topic
 * teaches (ADR 0106 §3). `avoidRadius` is the radius of the problem before it.
 */
export function buildCircleProblem(
    question: CircleQuestionOptions,
    maxRadius: number,
    key: number,
    language: PrintedLanguage = 'en',
    avoidRadius?: number
): ProblemData {
    const t = PROMPTS[language];
    const radius = radiusFor(usableMax(maxRadius), avoidRadius);
    const diameter = 2 * radius;

    let circle: CircleSpec;
    let display: string[];

    if (question === CircleQuestionOptions.DiameterFromCircumference) {
        // Built forwards - the diameter first, the circumference from it - so
        // the answer is whole by construction. The drawing hides the diameter.
        circle = { given: 'diameter', length: null, radius };
        display = [
            t.diameterFromCircumference(`${t.decimal(circumferenceHundredths(radius))}${NO_BREAK_SPACE}${LENGTH_UNIT}`),
            `${diameter} ${LENGTH_UNIT}`,
        ];
    } else {
        // Radius or diameter given, half each.
        const given: CircleSpec['given'] = getRandomInt(0, 1) === 0 ? 'radius' : 'diameter';
        circle = { given, length: given === 'radius' ? radius : diameter, radius };
        display =
            question === CircleQuestionOptions.Area
                ? [t.area(), `${t.decimal(areaHundredths(radius))} ${AREA_UNIT}`]
                : [t.circumference(), `${t.decimal(circumferenceHundredths(radius))} ${LENGTH_UNIT}`];
    }

    return {
        key,
        numbers: [],
        operator: "",
        display,
        circle,
        circleQuestion: question,
    };
}

// The same circle asked the same way is the same problem. The radius is in the
// fingerprint, not the label, so a radius of 5 and a diameter of 10 differ only
// in how they are drawn, which is a different problem to a student.
function fingerprint(problem: ProblemData): string {
    const circle = problem.circle!;
    return `${problem.circleQuestion}:${circle.given}:${circle.radius}`;
}

/** A page-shaped set of circle problems. */
export function generateCirclePages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.CircleQuestion ?? CircleQuestionOptions.Mixed;
    const maxRadius = settings.CircleMaxRadius ?? 10;
    const language = printedLanguageOf(settings);
    // The radius accepted at each position. `build` is called again at a key
    // until a problem is accepted there, so the last entry for key k is the
    // problem that stays, and key k + 1 avoids it.
    const radiusAt = new Map<number, number>();
    return generateProblemPages({
        settings,
        choices: questionTypes(question),
        build: (type, key) => {
            const problem = buildCircleProblem(type, maxRadius, key, language, radiusAt.get(key - 1));
            radiusAt.set(key, problem.circle!.radius);
            return problem;
        },
        fingerprint,
    });
}
